import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';
import { imageAttrs } from '../lib/imageUrl';

/** Cards whose photo already has AQA / OCR / Years pills baked in. */
const HAS_BAKED_TAGS = new Set();

// Three-column grid at lg+, two at md, one on mobile.
const COURSE_CARD_SIZES = '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw';

/**
 * Homepage & Courses page - three large photographic cards with information
 * pills at the top, a dark gradient overlay, white typography over the lower
 * half, a full-width yellow primary CTA, and a small secondary link.
 */
export default function CoursesSection() {
  const { courses } = useSite();
  return (
    <section className="py-14 md:py-20 bg-white" aria-labelledby="courses-heading">
      <div className="container">
        <div className="max-w-3xl">
          <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">Our courses</div>
          <h2
            id="courses-heading"
            className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
          >
            Science tuition from GCSE to A&nbsp;Level.
          </h2>
          <p className="mt-3 text-brand-ink/70 text-base md:text-lg">
            Live, structured GCSE and A&nbsp;Level tuition for AQA and OCR students.
          </p>
        </div>

        <div className="mt-10 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 px-2 sm:px-0 -mx-6 sm:mx-0">
          {courses.map((c) => (
            <article
              key={c.id}
              className="group relative flex flex-col rounded-3xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 focus-within:ring-2 focus-within:ring-brand-ink focus-within:ring-offset-2 bg-brand-ink min-h-[480px] sm:min-h-[520px] md:min-h-[560px]"
              data-testid={`course-card-${c.id}`}
            >
              {/* Full-bleed background image - eager so iOS Safari's lazy-
                  loading heuristic doesn't leave absolutely-positioned images
                  un-fetched. */}
              <img
                {...imageAttrs(c.image, { sizes: COURSE_CARD_SIZES, fallbackWidth: 800 })}
                alt={`${c.title} - student attending a Science Sorted lesson`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="eager"
                decoding="async"
              />

              {/* Dark gradient overlay for text legibility */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25"
                aria-hidden="true"
              />

              {/* Tag pills - natural flex child at the top so Safari can't
                  overlap them with the bottom content block. */}
              {!HAS_BAKED_TAGS.has(c.id) && Array.isArray(c.tags) && c.tags.length > 0 && (
                <div
                  className="relative z-10 p-3 md:p-4 flex flex-wrap gap-1.5"
                  data-testid={`course-tags-${c.id}`}
                >
                  {c.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur-sm px-2.5 py-1 text-xs md:text-sm font-medium text-brand-ink shadow-sm"
                    >
                      <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-brand-teal" aria-hidden="true" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Flex spacer pushes the content block to the bottom of the card. */}
              <div className="flex-grow" aria-hidden="true" />

              {/* Bottom content */}
              <div className="relative z-10 p-5 md:p-6 lg:p-7">
                <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
                  {c.title}
                </h3>
                <p className="mt-2 text-white/90 text-sm md:text-base leading-snug">
                  {c.body}
                </p>

                <a
                  href={BOOK_TRIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-brand-sun text-brand-ink hover:bg-white hover:text-brand-ink rounded-full py-3 md:py-3.5 font-semibold text-sm md:text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                >
                  Book a free trial
                  <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                </a>

                <Link
                  to="/courses"
                  className="mt-3 block text-center text-white/90 hover:text-white text-sm underline underline-offset-4 focus-visible:outline-none focus-visible:text-white"
                >
                  See all courses
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
